import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';

import { Separator } from '@/components/ui/separator';
import Grid from '@/components/inputs/CustomGrid';
import CustomButton from '@/components/inputs/CustomButton';
import CustomTextField from '@/components/inputs/CustomTextField';
import useLoading from '@/components/loading/hooks/useLoading';
import useSnackbar from '@/components/dialogs/snackbars/hooks/useSnackbar';
import useError from '@/components/validations/hooks/useError';
import Connections, { ApiEndpoints } from '@/components/connections/Connections';

const validations = {
  name: { type: 'required', text: 'field.notValidOrRequired' },
};

const defaultValues = {
  name: '',
  referenceNumber: '',
  eik: '',
  bulstat: '',
  vatId: '',
  accountablePerson: '',
  contactPerson: '',
  email: '',
  phone: '',
  address: { street: '', city: '', zip: '', country: '' },
  bankDetails: { iban: '', bic: '', bankName: '' },
  paymentTermDays: 14,
  discountPercentage: 0,
  notes: '',
};

const SectionTitle = ({ children }) => (
  <h2 className="text-base font-semibold mb-3">{children}</h2>
);

const CustomerForm = () => {
  const { companyId, customerId } = useParams();
  const isEdit = !!customerId;
  const [values, setValues] = useState(defaultValues);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, Loading, setLoading } = useLoading(isEdit);
  const { createSuccessSnackbar, createErrorSnackbar } = useSnackbar();
  const { isError, getActivateError } = useError({ values, validations });

  useEffect(() => {
    if (isEdit) {
      const loadCustomer = async () => {
        const res = await Connections.postRequest(ApiEndpoints.customersAll, { companyId });
        if (res.ok) {
          const customer = (res.data.customers || []).find(c => c._id === customerId);
          if (customer) {
            setValues({
              name: customer.name || '',
              referenceNumber: customer.referenceNumber || '',
              eik: customer.eik || '',
              bulstat: customer.bulstat || '',
              vatId: customer.vatId || '',
              accountablePerson: customer.accountablePerson || '',
              contactPerson: customer.contactPerson || '',
              email: customer.email || '',
              phone: customer.phone || '',
              address: customer.address || defaultValues.address,
              bankDetails: customer.bankDetails || defaultValues.bankDetails,
              paymentTermDays: customer.paymentTermDays ?? 14,
              discountPercentage: customer.discountPercentage ?? 0,
              notes: customer.notes || '',
            });
          }
        }
        setLoading(false);
      };
      loadCustomer().then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleChange = ({ name, value }) => {
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = ({ name, value }) => {
    setValues(prev => ({ ...prev, address: { ...prev.address, [name]: value } }));
  };

  const handleBankChange = ({ name, value }) => {
    setValues(prev => ({ ...prev, bankDetails: { ...prev.bankDetails, [name]: value } }));
  };

  const handleSave = async () => {
    const error = getActivateError();
    if (error) return;

    setLoading(true);
    const endpoint = isEdit ? ApiEndpoints.customerUpdate : ApiEndpoints.customerCreate;
    const params = isEdit
      ? { companyId, customerId, ...values }
      : { companyId, ...values };
    const res = await Connections.postRequest(endpoint, params);

    if (res.ok) {
      createSuccessSnackbar(t(isEdit ? 'customer.updated' : 'customer.created'));
      navigate(`/companies/${companyId}?tab=customers`);
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  if (loading) return <Loading />;

  const { address, bankDetails } = values;
  const backUrl = `/companies/${companyId}?tab=customers`;

  return (
    <>
      <Helmet>
        <title>{t('app.title')} | {t(isEdit ? 'customer.edit' : 'customer.create')}</title>
      </Helmet>
      <h1 className="text-2xl font-semibold mb-6">
        {t(isEdit ? 'customer.edit' : 'customer.create')}
      </h1>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fieldName="name" label={t('customer.name')} value={values.name}
            onChange={handleChange} fullWidth error={isError('name')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="referenceNumber" label={t('referenceNumber')} value={values.referenceNumber} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="eik" label={t('eik')} value={values.eik} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="bulstat" label={t('bulstat')} value={values.bulstat} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="vatId" label={t('vatId')} value={values.vatId} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="accountablePerson" label={t('accountablePerson')} value={values.accountablePerson} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="contactPerson" label={t('contactPerson')} value={values.contactPerson} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="email" label={t('email')} value={values.email} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="phone" label={t('phone')} value={values.phone} onChange={handleChange} fullWidth />
        </Grid>
      </Grid>

      <Separator className="my-6" />
      <SectionTitle>{t('address')}</SectionTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomTextField fieldName="street" label={t('address.street')} value={address.street} onChange={handleAddressChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="city" label={t('address.city')} value={address.city} onChange={handleAddressChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomTextField fieldName="zip" label={t('address.zip')} value={address.zip} onChange={handleAddressChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={1}>
          <CustomTextField fieldName="country" label={t('address.country')} value={address.country} onChange={handleAddressChange} fullWidth />
        </Grid>
      </Grid>

      <Separator className="my-6" />
      <SectionTitle>{t('bankDetails')}</SectionTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="iban" label={t('bank.iban')} value={bankDetails.iban} onChange={handleBankChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="bic" label={t('bank.bic')} value={bankDetails.bic} onChange={handleBankChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="bankName" label={t('bank.name')} value={bankDetails.bankName} onChange={handleBankChange} fullWidth />
        </Grid>
      </Grid>

      <Separator className="my-6" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="paymentTermDays" label={t('paymentTermDays')} value={values.paymentTermDays} onChange={handleChange} fullWidth type="number" />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="discountPercentage" label={t('discount')} value={values.discountPercentage} onChange={handleChange} fullWidth type="number" />
        </Grid>
        <Grid item xs={12}>
          <CustomTextField fieldName="notes" label={t('notes')} value={values.notes} onChange={handleChange} fullWidth multiline rows={3} />
        </Grid>
      </Grid>

      <div className="flex justify-end mt-8 gap-3">
        <CustomButton variant="outlined" onClick={() => navigate(backUrl)}>
          {t('cancel')}
        </CustomButton>
        <CustomButton onClick={handleSave}>
          {t(isEdit ? 'save' : 'create')}
        </CustomButton>
      </div>
    </>
  );
};

export default CustomerForm;
